import * as Dialog from '@radix-ui/react-dialog';
import { X, FileText, File, FileCode } from 'lucide-react';
import { useAppStore } from '../../lib/store';
import { exportToDocx, exportToPDF, exportToPlainText } from '../../lib/export';

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function ExportModal({ isOpen, onClose }: ExportModalProps) {
    const { activeNovelId, novels, themeProfile } = useAppStore();
    const novel = novels.find(n => n.id === activeNovelId);

    if (!novel) return null;

    const handleExportDocx = async () => {
        // In a real app we might prompt for author name, using a placeholder for now
        await exportToDocx(novel, "Author Name");
        onClose();
    };

    const handleExportPDF = () => {
        exportToPDF(novel);
        onClose();
    };

    const handleExportTxt = () => {
        exportToPlainText(novel);
        onClose();
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity" />
                <Dialog.Content
                    className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-full max-w-md p-6 rounded-2xl shadow-xl z-50 animate-in fade-in zoom-in duration-200"
                    style={{ backgroundColor: themeProfile.paperSurface, color: themeProfile.text, border: `1px solid ${themeProfile.border}` }}
                >
                    <div className="flex items-center justify-between mb-6">
                        <Dialog.Title className="text-xl font-semibold">
                            Export Manuscript
                        </Dialog.Title>
                        <Dialog.Close className="p-2 rounded-full hover:bg-black/5 transition-colors">
                            <X className="w-5 h-5" />
                        </Dialog.Close>
                    </div>

                    <p className="mb-6 opacity-80 leading-relaxed text-sm">
                        Download your work securely. All exports are generated entirely in your browser to protect your privacy.
                    </p>

                    <div className="space-y-3">
                        <ExportOption
                            icon={<FileText className="w-6 h-6 text-blue-500" />}
                            title="Microsoft Word (.docx)"
                            description="Industry standard manuscript format. 12pt Times New Roman, double-spaced."
                            onClick={handleExportDocx}
                            themeProfile={themeProfile}
                        />

                        <ExportOption
                            icon={<File className="w-6 h-6 text-red-500" />}
                            title="PDF Print (.pdf)"
                            description="Print-ready document exactly as it appears in Manuscript Mode."
                            onClick={handleExportPDF}
                            themeProfile={themeProfile}
                        />

                        <ExportOption
                            icon={<FileCode className="w-6 h-6 text-emerald-500" />}
                            title="Plain Text (.txt)"
                            description="Raw unformatted text. Universal fallback."
                            onClick={handleExportTxt}
                            themeProfile={themeProfile}
                        />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root >
    );
}

function ExportOption({ icon, title, description, onClick, themeProfile }: any) {
    return (
        <button
            onClick={onClick}
            className="w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ backgroundColor: themeProfile.background, border: `1px solid ${themeProfile.border}` }}
        >
            <div className="p-3 rounded-lg bg-black/5">
                {icon}
            </div>
            <div>
                <h3 className="font-medium mb-1">{title}</h3>
                <p className="text-sm opacity-70 leading-snug">{description}</p>
            </div>
        </button>
    );
}
